const GUEST_CART_STORAGE_KEY = "shopify_guest_cart_v1";
export const CART_UPDATED_EVENT = "gootocart_cart_updated";
export const DRAWER_TOGGLE_EVENT = "gootocart_drawer_toggle";

/**
 * Get current items in guest cart
 */
export const getGuestCart = () => {
  try {
    const raw = localStorage.getItem(GUEST_CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const retainVisibleCartProducts = (visibleIds) => {
  const allowed = new Set((visibleIds || []).map((id) => String(id)));
  const cart = getGuestCart();
  const next = cart.filter((item) => {
    const raw = item.product_id || item.id;
    const productId = String(raw).split(":")[0];
    return allowed.has(productId);
  });
  if (next.length !== cart.length) {
    saveAndNotify(next);
  }
  return next;
};

/**
 * Save cart items and dispatch update event
 */
const saveAndNotify = (items) => {
  try {
    localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT, { detail: items }));
  } catch (err) {
    console.error("Cart save error:", err);
  }
};

/**
 * Add or increment an item in the guest cart
 */
export const addToGuestCart = (product, qty = 1, variant = null) => {
  const current = getGuestCart();
  const prodId = product.id || product.product_id;
  const lineId = variant?.id ? `${prodId}:${variant.id}` : prodId;
  const existingIdx = current.findIndex((item) => String(item.id) === String(lineId));

  const pricing = product.pricing || {};
  let unitPrice = Number(
    pricing.final_price ?? product.price ?? product.sellingPrice ?? product.productprices?.seeling_price ?? 0
  );
  let originalPrice = Number(
    pricing.original_price ?? product.original_price ?? product.productprices?.seeling_price ?? unitPrice
  );
  let stockQty = Number(pricing.stock_qty ?? product.productprices?.qty ?? 20);
  let name = product.name || "Product";

  if (variant) {
    unitPrice = Number(variant.final_price ?? variant.price ?? unitPrice);
    originalPrice = Number(variant.price ?? unitPrice);
    if (variant.compare_price && Number(variant.compare_price) > originalPrice) {
      originalPrice = Number(variant.compare_price);
    }
    stockQty = Number(variant.qty ?? 0);
    name = `${product.name || "Product"} — ${variant.title}`;
  }

  const hasDiscount = variant
    ? originalPrice > unitPrice
    : (pricing.has_discount ?? originalPrice > unitPrice);
  const discountLabel = pricing.discount_label || "";

  const image =
    product.image ||
    product.product_image?.[0]?.image ||
    product.productImage?.[0]?.image ||
    product.product_image ||
    "";

  if (existingIdx > -1) {
    const newQty = Math.min(stockQty, current[existingIdx].qty + qty);
    current[existingIdx] = {
      ...current[existingIdx],
      qty: newQty,
      price: unitPrice,
      originalPrice,
    };
  } else {
    current.push({
      id: lineId,
      product_id: prodId,
      variant_id: variant?.id || null,
      name,
      slug: product.slug || "",
      categorySlug: product.category?.slug || product.categorySlug || "",
      categoryName: product.category?.name || product.categoryName || "",
      image,
      price: unitPrice,
      originalPrice,
      hasDiscount,
      discountLabel,
      qty: Math.min(stockQty, Math.max(1, qty)),
      stockQty,
      product_price_id: product.productprices?.id || product.product_price_id || null,
      discount_id:
        product.discount_id ||
        product.discount_product?.id ||
        product.discountProduct?.id ||
        product.category?.discountProduct?.id ||
        null,
    });
  }

  saveAndNotify(current);
  return current;
};

/**
 * Update specific item quantity in guest cart
 */
export const updateGuestCartQty = (productId, newQty) => {
  let current = getGuestCart();
  if (newQty <= 0) {
    current = current.filter((item) => String(item.id) !== String(productId));
  } else {
    current = current.map((item) => {
      if (String(item.id) === String(productId)) {
        const cappedQty = Math.min(item.stockQty || 50, newQty);
        return { ...item, qty: cappedQty };
      }
      return item;
    });
  }
  saveAndNotify(current);
  return current;
};

/**
 * Remove an item from the cart
 */
export const removeGuestCartItem = (productId) => {
  const current = getGuestCart().filter((item) => String(item.id) !== String(productId));
  saveAndNotify(current);
  return current;
};

/**
 * Empty the cart
 */
export const clearGuestCart = () => {
  saveAndNotify([]);
};

const FREE_SHIPPING_THRESHOLD_STORAGE_KEY = "store_free_shipping_threshold";

/**
 * Get dynamic store free shipping threshold (synced from store settings)
 */
export const getStoreFreeShippingThreshold = () => {
  try {
    const val = localStorage.getItem(FREE_SHIPPING_THRESHOLD_STORAGE_KEY);
    if (val !== null && val !== "" && !isNaN(Number(val))) {
      return Number(val);
    }
  } catch {}
  return 2500;
};

/**
 * Update dynamic store free shipping threshold in local storage
 */
export const setStoreFreeShippingThreshold = (threshold) => {
  try {
    if (threshold !== undefined && threshold !== null && !isNaN(Number(threshold))) {
      localStorage.setItem(FREE_SHIPPING_THRESHOLD_STORAGE_KEY, String(threshold));
      // Notify listeners so UI recalculates immediately
      window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT, { detail: getGuestCart() }));
    }
  } catch {}
};

/**
 * Get total quantity count of all items
 */
export const getGuestCartCount = () => {
  const cart = getGuestCart();
  return cart.reduce((sum, item) => sum + (Number(item.qty) || 1), 0);
};

/**
 * Calculate financial totals of current cart with dynamic free shipping threshold
 */
export const getGuestCartTotals = (customThreshold) => {
  const freeShippingThreshold =
    customThreshold !== undefined && customThreshold !== null && !isNaN(Number(customThreshold))
      ? Number(customThreshold)
      : getStoreFreeShippingThreshold();

  const cart = getGuestCart();
  let subtotal = 0;
  let originalTotal = 0;
  let totalCount = 0;

  cart.forEach((item) => {
    const q = Number(item.qty) || 1;
    const p = Number(item.price) || 0;
    const orig = Number(item.originalPrice) || p;
    subtotal += p * q;
    originalTotal += orig * q;
    totalCount += q;
  });

  const totalSavings = Math.max(0, originalTotal - subtotal);
  const isFreeShipping = freeShippingThreshold === 0 ? true : subtotal >= freeShippingThreshold;
  const shippingFee = isFreeShipping || subtotal === 0 ? 0 : 200;
  const remainingForFreeShipping = freeShippingThreshold === 0 ? 0 : Math.max(0, freeShippingThreshold - subtotal);
  const freeShippingProgress =
    freeShippingThreshold === 0
      ? 100
      : Math.min(100, (subtotal / freeShippingThreshold) * 100);

  return {
    items: cart,
    subtotal,
    originalTotal,
    totalSavings,
    shippingFee,
    isFreeShipping,
    freeShippingThreshold,
    remainingForFreeShipping,
    freeShippingProgress,
    grandTotal: subtotal + shippingFee,
    totalCount,
  };
};

/**
 * Trigger slide-out Cart Drawer
 */
export const openCartDrawer = () => {
  window.dispatchEvent(new CustomEvent(DRAWER_TOGGLE_EVENT, { detail: { open: true } }));
};

export const closeCartDrawer = () => {
  window.dispatchEvent(new CustomEvent(DRAWER_TOGGLE_EVENT, { detail: { open: false } }));
};
